import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import ODataTable from '../../components/reusable/ODataTable'
import OPagination from '../../components/reusable/OPagination'
import { AiOutlineEye, AiOutlineDelete } from 'react-icons/ai'
import { apiDelete } from '../../utils/apiFetch'
import helpers from '../../utils/helpers'

const OnboardingCandidates = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1)
  const keyword = searchParams.get('keyword') || ''

  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pageCount: 0, totalItems: 0, limit: 10 })
  const [sort, setSort] = useState({ sortKey: 'createdAt', sortType: 'desc' })
  const [keywordInput, setKeywordInput] = useState(keyword)

  const updateParams = (patch, { resetPage = false } = {}) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        next.delete(key)
      } else {
        next.set(key, String(value))
      }
    })
    if (resetPage) next.set('page', '1')
    setSearchParams(next)
  }

  const fetchCandidates = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      params.set('page', page)
      params.set('pageSize', pagination.limit)
      if (sort?.sortKey) params.set('sortKey', sort.sortKey)
      if (sort?.sortType) params.set('sortType', sort.sortType)
      if (keyword) params.set('keyword', keyword)
      
      const res = await apiGet(`${apiPath.onboardingCandidatesList}?${params.toString()}`)
      const results = res?.data?.results || {}
      const items = results?.docs || []
      setList(items)
      setPagination({
        page: results.page || page,
        pageCount: results.totalPages || 0,
        totalItems: results.totalDocs || 0,
        limit: results.limit || pagination.limit
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCandidates()
  }, [page, keyword, sort])

  useEffect(() => {
    setKeywordInput(keyword)
  }, [keyword])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (keywordInput !== keyword) {
        updateParams({ keyword: keywordInput }, { resetPage: true })
      }
    }, 300)
    return () => clearTimeout(handler)
  }, [keywordInput])

  const onPageChange = ({ selected }) => {
    updateParams({ page: selected + 1 })
  }

  const deleteCandidate = async (id) => {
    try {
      setLoading(true)
      const res = await apiDelete(`${apiPath.onboardingCandidatesList}/${id}`)
      if (res?.data?.success) {
        await fetchCandidates()
      }
    } catch (error) {
      console.error('Delete error:', error)
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    { title: t('O_FULL_NAME') || 'Full Name', key: 'fullNameArabic', sortable: true, render: (row) => row?.fullNameArabic || '-' },
    { title: t('O_EMAIL_ADDRESS') || 'Email Address', key: 'email', sortable: true, render: (row) => row?.email || '-' },
    { title: t('CAREERS_APPLIED_AT') || 'Applied At', key: 'createdAt', sortable: true, render: (row) => new Date(row.createdAt).toLocaleString() },
    { title: t('O_ACTION') || 'Action', key: 'actions', align: 'center', render: (row) => {
        return (
            <div className='flex items-center gap-3 justify-center w-full'>
                <button
                    className="hover:text-blue-700 w-[18px] h-[18px] flex items-center justify-center transition-colors text-gray-500"
                    title={t("O_VIEW") || 'View'}
                    onClick={() => navigate(`/onboarding-requests/view/${row._id}`)}
                >
                    <AiOutlineEye size={18} />
                </button>
                <button
                    className="hover:text-red-600 w-[18px] h-[18px] flex items-center justify-center transition-colors text-gray-500"
                    title={t("O_DELETE") || 'Delete'}
                    onClick={() =>
                        helpers.alertFunction(
                            t('O_ARE_YOU_SURE_DELETE') || 'Are you sure you want to delete this candidate?',
                            row._id,
                            deleteCandidate,
                            true
                        )
                    }
                >
                    <AiOutlineDelete size={18} />
                </button>
            </div>
        )
    }}
  ]

  return (
    <div>
      <div className='bg-white'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9]'>
            <div className='border-b border-b-[#E3E3E3] flex flex-wrap items-end gap-2 px-4 py-3'>
              <div className='w-full md:w-60'>
                <label className='block mb-2 text-sm font-medium text-gray-900'>{t('O_SEARCH')}</label>
                <input
                  className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
                  placeholder={t('SEARCH_BY_KEYWORD')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                />
              </div>
            </div>

            <ODataTable
              data={list}
              columns={columns}
              loading={loading}
              pagination={pagination}
              onPageChange={onPageChange}
              sort={sort}
              onSortChange={(nextSort) => {
                setSort(nextSort)
                updateParams({ page: 1 })
              }}
            />

            <OPagination pagination={pagination} onPageChange={onPageChange} page={pagination.page} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingCandidates
